/* eslint-disable react/prop-types */
import React from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { FaXmark } from "react-icons/fa6";
import { Utensils, Tag, AlertCircle, Sparkles, CheckCircle2, XCircle, ArrowRightLeft, Layers } from "lucide-react";
import NoImage from "../../assets/images/no-img.png";
import { getCustomizationList, getOrderItemDisplayPrice, getNormalizedSelectedCourses } from "./OrderItemComboBreakdown";

export default function OrderedItemDetailsModal({ isOpen, onClose, item, orderType = "", isTabletView = false }) {
    if (!isOpen || !item) return null;

    const priceInfo = getOrderItemDisplayPrice(item, orderType);
    const customizations = getCustomizationList(item);

    const itemName = item.name || item.item_name || item.display_name || item.create_your_own_general?.name || item.banquet?.name || "Item Details";
    const itemNo = item.item_no || item.menu_item?.item_no || item.create_your_own_general?.item_no || item.banquet?.item_no || null;
    const variantName = item.variant?.name || item.variant_name || null;
    const sizeName = item.size || null;
    const buffetFor = item.buffet_price_for ? (item.buffet_price_for === "adult" ? "Adult Buffet" : "Kids Buffet") : null;

    const customPizza = item.custom_pizza || item.data?.item?.custom_pizza || null;
    const pizzaBase = (customPizza?.base_name || customPizza?.base_id) ? {
        id: customPizza.base_id,
        name: customPizza.base_name,
        price: Number(customPizza.base_price || 0)
    } : (item.pizza_base || item.base || item.customPizzaData?.base || item.data?.item?.base || null);

    const pizzaCrust = (customPizza?.crust_name || customPizza?.crust_id) ? {
        id: customPizza.crust_id,
        name: customPizza.crust_name,
        price: Number(customPizza.crust_price || 0)
    } : (item.pizza_crust || item.crust || item.customPizzaData?.crust || item.data?.item?.crust || null);

    const quantity = Number(item.quantity || 1);
    const unitPrice = Number(priceInfo.price || 0);
    const totalPrice = (unitPrice * quantity).toFixed(2);

    const itemImage = item.file_manager_url || item.image || item.create_your_own_general?.file_manager_url || item.banquet?.file_manager_url || NoImage;

    // Get ONLY normalized selected courses and items
    const selectedCourses = getNormalizedSelectedCourses(item);
    const hasSelectedCourses = Array.isArray(selectedCourses) && selectedCourses.length > 0;
    const totalCourseItemsCount = selectedCourses.reduce((sum, c) => sum + (c.items?.length || 0), 0);

    const mealDescription = item.create_your_own_general?.description || item.banquet?.description || item.special_menu?.description || item.description || null;

    // Special Menu (Sunday Roast, Christmas, Buffet, Platter, Dish of the Day, etc.)
    const specialMenu = item.special_menu || null;
    const specialMenuItems = Array.isArray(specialMenu?.menu_items) ? specialMenu.menu_items : [];
    const specialIngredients = Array.isArray(specialMenu?.ingredients) ? specialMenu.ingredients : [];
    const hasSpecialMenu = specialMenuItems.length > 0 || specialIngredients.length > 0 || Boolean(specialMenu?.description);

    // Banquet Extra Items
    const banquetExtraItems = Array.isArray(item.banquet_extra_items) ? item.banquet_extra_items : [];
    const hasBanquetExtraItems = banquetExtraItems.length > 0;
    const banquetExtraPrice = Number(item.banquet_extra_price || 0);

    // Combos & Mix and Match
    const hasCombos = Array.isArray(item.combo_items) && item.combo_items.length > 0;
    const hasMixAndMatch = Array.isArray(item.mix_and_match_items || item.selected_items || item.categories) &&
        (item.mix_and_match_items || item.selected_items || item.categories).length > 0;

    const itemNote = item.notes || item.note || item.special_instructions || item.item_note || null;
    const cookingNotes = item.cooking_preferences || item.cooking_notes || null;

    // Group customizations by category (exclude base and crust since they appear in Specifications, exclude banquet if handled separately)
    const categorizedCustomizations = {
        toppings: customizations.filter(c => c.category?.toLowerCase().includes("topping")),
        sauces: customizations.filter(c => c.category?.toLowerCase().includes("sauce")),
        salads: customizations.filter(c => c.category?.toLowerCase().includes("salad")),
        addons: customizations.filter(c => c.category?.toLowerCase().includes("addon")),
        modifiers: customizations.filter(c => c.category?.toLowerCase().includes("modifier")),
        banquet: (hasSelectedCourses || hasBanquetExtraItems) ? [] : customizations.filter(c => c.category?.toLowerCase().includes("banquet")),
        others: customizations.filter(c => 
            !c.category?.toLowerCase().includes("topping") &&
            !c.category?.toLowerCase().includes("sauce") &&
            !c.category?.toLowerCase().includes("salad") &&
            !c.category?.toLowerCase().includes("addon") &&
            !c.category?.toLowerCase().includes("modifier") &&
            !c.category?.toLowerCase().includes("banquet") &&
            !c.category?.toLowerCase().includes("base") &&
            !c.category?.toLowerCase().includes("crust")
        ),
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent 
                className={`bg-color2 border border-color3 text-white rounded-xl shadow-2xl flex flex-col overflow-hidden max-h-[92vh] w-[95vw] ${
                    isTabletView ? "max-w-[620px]" : "max-w-[780px]"
                } p-0 z-[130] [&>button]:hidden`}
            >
                <DialogTitle className="sr-only">{itemName}</DialogTitle>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 bg-color3/60 border-b border-color3 shrink-0">
                    <div className="flex items-center gap-2.5 flex-wrap">
                        {itemNo && (
                            <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-bold rounded">
                                #{itemNo}
                            </span>
                        )}
                        <h2 className="text-lg md:text-xl font-bold text-white tracking-wide flex items-center gap-2">
                            <span>{itemName}</span>
                        </h2>
                    </div>

                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full bg-color2/80 hover:bg-red-500/20 text-gray-300 hover:text-red-400 flex items-center justify-center transition cursor-pointer border border-color3"
                        title="Close"
                    >
                        <FaXmark size={18} />
                    </button>
                </div>

                {/* Status Badges Row */}
                {(item.is_complementary || item.is_loyalty_free || item.is_offer_free || item.is_offer_discount || item.is_replaced || hasSelectedCourses || hasCombos || hasSpecialMenu || hasBanquetExtraItems) && (
                    <div className="px-5 py-2 bg-color3/30 border-b border-color3/50 flex flex-wrap gap-2 items-center text-xs">
                        {hasSelectedCourses && (
                            <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full font-semibold flex items-center gap-1">
                                <Layers size={13} /> Selected Course Meal
                            </span>
                        )}
                        {hasCombos && (
                            <span className="px-2.5 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full font-semibold flex items-center gap-1">
                                <Utensils size={13} /> Combo Meal
                            </span>
                        )}
                        {hasSpecialMenu && (
                            <span className="px-2.5 py-0.5 bg-amber-400/20 text-amber-300 border border-amber-400/40 rounded-full font-semibold flex items-center gap-1">
                                <Sparkles size={13} /> Special Menu
                            </span>
                        )}
                        {hasBanquetExtraItems && (
                            <span className="px-2.5 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/40 rounded-full font-semibold flex items-center gap-1">
                                <Utensils size={13} /> Banquet Extra Items ({banquetExtraItems.length})
                            </span>
                        )}
                        {item.is_complementary && (
                            <span className="px-2.5 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/40 rounded-full font-semibold flex items-center gap-1">
                                <Sparkles size={13} /> Complementary Item
                            </span>
                        )}
                        {(item.is_loyalty_free || item.is_offer_free || priceInfo.isFree) && !item.is_complementary && (
                            <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-full font-semibold flex items-center gap-1">
                                <CheckCircle2 size={13} /> Free Item
                            </span>
                        )}
                        {item.is_offer_discount && (
                            <span className="px-2.5 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full font-semibold flex items-center gap-1">
                                <Tag size={13} /> Discount Offer (-£{item.offer_discount_amount || priceInfo.discountAmount || 0})
                            </span>
                        )}
                        {item.is_replaced && (
                            <span className="px-2.5 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/40 rounded-full font-semibold flex items-center gap-1">
                                <ArrowRightLeft size={13} /> Replaced Item
                            </span>
                        )}
                    </div>
                )}

                {/* Modal Scrollable Body */}
                <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4 text-sm">
                    
                    {/* Top Overview & Pricing Card */}
                    <div className="bg-color3/40 border border-color3 rounded-lg p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3.5">
                            <div className="w-16 h-16 rounded-lg bg-color2 overflow-hidden border border-color3 shrink-0 flex items-center justify-center">
                                <img
                                    src={itemImage}
                                    alt={itemName}
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.currentTarget.src = NoImage; }}
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <div className="text-base font-semibold text-white">{itemName}</div>
                                {mealDescription && (
                                    <div className="text-xs text-gray-300 italic">{mealDescription}</div>
                                )}
                                <div className="flex flex-wrap gap-1.5 text-xs mt-0.5">
                                    {variantName && (
                                        <span className="px-2 py-0.5 bg-color2 text-color6 border border-color3 rounded font-medium">
                                            Variant: {variantName}
                                        </span>
                                    )}
                                    {sizeName && (
                                        <span className="px-2 py-0.5 bg-color2 text-color6 border border-color3 rounded font-medium">
                                            Size: {sizeName}
                                        </span>
                                    )}
                                    {buffetFor && (
                                        <span className="px-2 py-0.5 bg-color2 text-amber-400 border border-color3 rounded font-medium">
                                            {buffetFor}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Price Breakdown */}
                        <div className="w-full sm:w-auto flex sm:flex-col items-center sm:items-end justify-between sm:justify-center border-t sm:border-t-0 pt-2 sm:pt-0 border-color3/60 sm:border-l sm:pl-4 shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">Unit Price:</span>
                                <span className="text-base font-bold text-color6">
                                    {priceInfo.isFree ? "Free" : `£${unitPrice.toFixed(2)}`}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-400">Quantity:</span>
                                <span className="text-xs font-semibold text-white bg-color2 px-2 py-0.5 rounded border border-color3">
                                    x{quantity}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs font-medium text-gray-300">Total:</span>
                                <span className="text-lg font-extrabold text-amber-400">
                                    {priceInfo.isFree ? "£0.00" : `£${totalPrice}`}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Specific Attributes (Base / Crust / Modifiers) */}
                    {(pizzaBase || pizzaCrust || cookingNotes) && (
                        <div className="bg-color3/30 border border-color3/60 rounded-lg p-3 flex flex-col gap-2">
                            <div className="text-xs font-semibold text-color6 uppercase tracking-wider">Specifications</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                {pizzaBase && (
                                    <div className="flex items-center justify-between bg-color2/60 p-2 rounded border border-color3/40">
                                        <span className="text-gray-400">Pizza Base:</span>
                                        <span className="text-white font-medium flex items-center gap-1">
                                            <span>{typeof pizzaBase === "object" ? pizzaBase.name : pizzaBase}</span>
                                            {typeof pizzaBase === "object" && Number(pizzaBase.price) > 0 && (
                                                <span className="text-amber-400 font-semibold">(+£{Number(pizzaBase.price).toFixed(2)})</span>
                                            )}
                                        </span>
                                    </div>
                                )}
                                {pizzaCrust && (
                                    <div className="flex items-center justify-between bg-color2/60 p-2 rounded border border-color3/40">
                                        <span className="text-gray-400">Pizza Crust:</span>
                                        <span className="text-white font-medium flex items-center gap-1">
                                            <span>{typeof pizzaCrust === "object" ? pizzaCrust.name : pizzaCrust}</span>
                                            {typeof pizzaCrust === "object" && Number(pizzaCrust.price) > 0 && (
                                                <span className="text-amber-400 font-semibold">(+£{Number(pizzaCrust.price).toFixed(2)})</span>
                                            )}
                                        </span>
                                    </div>
                                )}
                                {cookingNotes && (
                                    <div className="flex items-center justify-between bg-color2/60 p-2 rounded border border-color3/40 sm:col-span-2">
                                        <span className="text-gray-400">Cooking Preference:</span>
                                        <span className="text-white font-medium">{cookingNotes}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Selected Course Items (ONLY SELECTED ITEMS PER COURSE) */}
                    {hasSelectedCourses && (
                        <div className="bg-color3/30 border border-emerald-500/40 rounded-lg p-3.5 flex flex-col gap-3">
                            <div className="flex items-center justify-between border-b border-color3/60 pb-1.5">
                                <div className="text-xs font-semibold text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Layers size={14} /> Selected Course Items ({totalCourseItemsCount})
                                </div>
                                {mealDescription && (
                                    <span className="text-[11px] text-gray-400 italic">
                                        {mealDescription}
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-col gap-2.5">
                                {selectedCourses.map((course, cIdx) => (
                                    <div 
                                        key={course.id || cIdx}
                                        className="bg-color2/90 border border-color3 rounded-lg p-3 flex flex-col gap-2"
                                    >
                                        <div className="flex items-center justify-between text-xs font-bold text-emerald-300 border-b border-color3/50 pb-1">
                                            <span>{cIdx + 1}. {course.name}</span>
                                            <span className="text-[10px] text-gray-400 font-normal">
                                                {course.items.length} item{course.items.length > 1 ? "s" : ""} selected
                                            </span>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            {course.items.map((ci, sIdx) => {
                                                const ciName = ci.name || ci.display_name || ci.item_name || ci.menu_item?.name || "Item";
                                                const isReplaced = Boolean(ci.is_replaced);
                                                const activeExtra = Number(ci.price_difference ?? ci.replacement_extra_out_price ?? ci.replacement_extra_price ?? 0);
                                                const ciImage = ci.file_manager_url || ci.menu_item?.file_manager_url || null;
                                                const subCustomizations = getCustomizationList(ci);

                                                return (
                                                    <div 
                                                        key={ci.id || sIdx}
                                                        className="bg-color3/50 rounded-lg p-2.5 border border-color3 flex flex-col gap-1 text-xs"
                                                    >
                                                        <div className="flex items-center justify-between flex-wrap gap-1">
                                                            <div className="flex items-center gap-2.5 flex-wrap">
                                                                {ciImage && (
                                                                    <img 
                                                                        src={ciImage} 
                                                                        alt={ciName}
                                                                        className="w-7 h-7 rounded object-cover border border-color3 shrink-0" 
                                                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                                                    />
                                                                )}
                                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                                    <span className="text-emerald-400 font-bold">✓</span>
                                                                    <span className="font-semibold text-white">{ciName}</span>
                                                                    {ci.variant?.name && (
                                                                        <span className="text-color6">({ci.variant.name})</span>
                                                                    )}
                                                                    <span className="text-xs text-gray-400 bg-color2 px-1.5 py-0.2 rounded border border-color3">
                                                                        x{ci.quantity || 1}
                                                                    </span>
                                                                    {isReplaced && (
                                                                        <span className="text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/40 px-1.5 py-0.2 rounded font-bold">
                                                                            Replaced
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {isReplaced && activeExtra > 0 && (
                                                                <span className="text-xs text-amber-400 font-bold">
                                                                    +£{activeExtra.toFixed(2)}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {isReplaced && ci.original_item && (
                                                            <div className="text-[11px] text-gray-400 italic pl-3 border-l border-amber-500/40">
                                                                Original: {ci.original_item.display_name || ci.original_item.name}
                                                                {ci.original_item.variant?.name ? ` (${ci.original_item.variant.name})` : ""}
                                                            </div>
                                                        )}

                                                        {subCustomizations.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 pt-1 pl-3 border-l border-color3 text-[11px]">
                                                                {subCustomizations.map((cust, idx) => {
                                                                    const isExtra = cust.isExtra;
                                                                    const isCanceled = cust.isCanceled;
                                                                    const price = Number(cust.price || 0);

                                                                    if (isCanceled) {
                                                                        return (
                                                                            <span
                                                                                key={cust.id || idx}
                                                                                className="px-1.5 py-0.5 rounded bg-red-950/40 text-red-400 border border-red-800/50 line-through flex items-center gap-1"
                                                                                title="Removed / Canceled"
                                                                            >
                                                                                <XCircle size={10} className="shrink-0" />
                                                                                {cust.name}
                                                                            </span>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <span
                                                                            key={cust.id || idx}
                                                                            className={`px-1.5 py-0.5 rounded ${
                                                                                isExtra
                                                                                    ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                                                                                    : "bg-color2 text-gray-300"
                                                                            }`}
                                                                        >
                                                                            + {cust.name} {isExtra && price > 0 ? `(£${price.toFixed(2)})` : ""}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Banquet Extra Items */}
                    {hasBanquetExtraItems && (
                        <div className="bg-color3/30 border border-blue-500/40 rounded-lg p-3.5 flex flex-col gap-3">
                            <div className="flex items-center justify-between border-b border-color3/60 pb-1.5">
                                <div className="text-xs font-semibold text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Utensils size={14} /> Banquet Extra Items ({banquetExtraItems.length})
                                </div>
                                {banquetExtraPrice > 0 && (
                                    <span className="text-xs font-bold text-amber-400">
                                        Total Extra: +£{banquetExtraPrice.toFixed(2)}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                {banquetExtraItems.map((extra, eIdx) => {
                                    const extraName = extra.display_name || extra.name || extra.menu_item?.name || "Extra Item";
                                    const extraCharge = Number(extra.extra_charge ?? extra.price ?? 0);
                                    const extraImage = extra.file_manager_url || extra.menu_item?.file_manager_url || null;

                                    return (
                                        <div
                                            key={extra.id || eIdx}
                                            className="bg-color2/90 rounded-lg p-2.5 border border-color3 flex items-center justify-between gap-2 text-xs"
                                        >
                                            <div className="flex items-center gap-2.5 flex-wrap">
                                                {extraImage && (
                                                    <img
                                                        src={extraImage}
                                                        alt={extraName}
                                                        className="w-7 h-7 rounded object-cover border border-color3 shrink-0"
                                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                                    />
                                                )}
                                                <span className="text-blue-400 font-bold">+</span>
                                                <span className="font-semibold text-white">{extraName}</span>
                                                <span className="text-xs text-gray-400 bg-color3 px-1.5 py-0.5 rounded border border-color3/50">
                                                    x{extra.quantity || 1}
                                                </span>
                                            </div>
                                            {extraCharge > 0 && (
                                                <span className="text-xs font-bold text-amber-400">
                                                    +£{extraCharge.toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Special Menu Items Breakdown (Sunday Roast, Christmas, Buffet, Platter, Dish of Day, etc.) */}
                    {hasSpecialMenu && (
                        <div className="bg-color3/30 border border-amber-400/40 rounded-lg p-3.5 flex flex-col gap-3">
                            <div className="flex items-center justify-between border-b border-color3/60 pb-1.5">
                                <div className="text-xs font-semibold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Sparkles size={14} /> Special Menu Items ({specialMenuItems.length})
                                </div>
                                {specialMenu?.description && (
                                    <span className="text-[11px] text-gray-400 italic">
                                        {specialMenu.description}
                                    </span>
                                )}
                            </div>

                            {specialMenuItems.length > 0 && (
                                <div className="flex flex-col gap-2">
                                    {specialMenuItems.map((mi, mIdx) => {
                                        const miName = mi.display_name || mi.name || mi.item_name || mi.menu_item?.name || "Item";
                                        const variantName = mi.variant?.name || mi.variant_name || null;
                                        const miImage = mi.file_manager_url || mi.menu_item?.file_manager_url || null;
                                        const subCustomizations = getCustomizationList(mi);

                                        return (
                                            <div
                                                key={mi.id || mIdx}
                                                className="bg-color2/90 rounded-lg p-2.5 border border-color3 flex flex-col gap-1.5 text-xs"
                                            >
                                                <div className="flex items-center justify-between flex-wrap gap-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        {miImage && (
                                                            <img
                                                                src={miImage}
                                                                alt={miName}
                                                                className="w-7 h-7 rounded object-cover border border-color3 shrink-0"
                                                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                                                            />
                                                        )}
                                                        <span className="text-amber-400 font-bold">•</span>
                                                        <span className="font-semibold text-white">{miName}</span>
                                                        {variantName && !miName.includes(`(${variantName})`) && (
                                                            <span className="text-color6">({variantName})</span>
                                                        )}
                                                        <span className="text-xs text-gray-400 bg-color3 px-1.5 py-0.5 rounded border border-color3/50">
                                                            x{mi.quantity || 1}
                                                        </span>
                                                    </div>
                                                </div>

                                                {subCustomizations.length > 0 && (
                                                    <div className="flex flex-wrap gap-1 pt-1 pl-3 border-l border-color3 text-[11px]">
                                                        {subCustomizations.map((cust, idx) => {
                                                            const isExtra = cust.isExtra;
                                                            const isCanceled = cust.isCanceled;
                                                            const price = Number(cust.price || 0);

                                                            if (isCanceled) {
                                                                return (
                                                                    <span
                                                                        key={cust.id || idx}
                                                                        className="px-1.5 py-0.5 rounded bg-red-950/40 text-red-400 border border-red-800/50 line-through flex items-center gap-1"
                                                                        title="Removed / Canceled"
                                                                    >
                                                                        <XCircle size={10} className="shrink-0" />
                                                                        {cust.name}
                                                                    </span>
                                                                );
                                                            }

                                                            return (
                                                                <span
                                                                    key={cust.id || idx}
                                                                    className={`px-1.5 py-0.5 rounded ${
                                                                        isExtra
                                                                            ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                                                                            : "bg-color3 text-gray-300"
                                                                    }`}
                                                                >
                                                                    + {cust.name} {isExtra && price > 0 ? `(£${price.toFixed(2)})` : ""}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {specialIngredients.length > 0 && (
                                <div className="flex flex-col gap-1.5 bg-color2/70 p-2.5 rounded-lg border border-color3">
                                    <span className="text-[11px] font-semibold text-gray-300">Ingredients:</span>
                                    <div className="flex flex-wrap gap-1.5">
                                        {specialIngredients.map((ing, iIdx) => {
                                            const ingName = typeof ing === "string" ? ing : (ing.name || ing.ingredient_name || "");
                                            if (!ingName) return null;
                                            return (
                                                <span key={iIdx} className="px-2 py-0.5 bg-color3 text-gray-200 border border-color3/60 rounded text-xs">
                                                    • {ingName}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Customizations & Modifications Section */}
                    {Object.values(categorizedCustomizations).some(list => list.length > 0) && (
                        <div className="bg-color3/30 border border-color3/60 rounded-lg p-3.5 flex flex-col gap-3">
                            <div className="flex items-center justify-between border-b border-color3/60 pb-1.5">
                                <div className="text-xs font-semibold text-color6 uppercase tracking-wider flex items-center gap-1.5">
                                    <Sparkles size={14} /> Customizations & Add-ons
                                </div>
                                <span className="text-[11px] text-gray-400 font-medium">
                                    {Object.values(categorizedCustomizations).reduce((acc, list) => acc + list.length, 0)} modification{Object.values(categorizedCustomizations).reduce((acc, list) => acc + list.length, 0) > 1 ? "s" : ""}
                                </span>
                            </div>

                            <div className="flex flex-col gap-2.5">
                                {Object.entries(categorizedCustomizations).map(([categoryKey, list]) => {
                                    if (list.length === 0) return null;
                                    const categoryLabel = categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);

                                    return (
                                        <div key={categoryKey} className="flex flex-col gap-1.5">
                                            <div className="text-[11px] font-semibold text-gray-400">
                                                {categoryLabel}:
                                            </div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {list.map((cust, idx) => {
                                                    const isExtra = cust.isExtra;
                                                    const isCanceled = cust.isCanceled;
                                                    const price = Number(cust.price || 0);

                                                    if (isCanceled) {
                                                        return (
                                                            <span
                                                                key={cust.id || idx}
                                                                className="px-2 py-1 rounded bg-red-950/40 text-red-400 border border-red-800/50 line-through text-xs flex items-center gap-1"
                                                                title="Removed / Canceled"
                                                            >
                                                                <XCircle size={12} className="shrink-0" />
                                                                {cust.name}
                                                            </span>
                                                        );
                                                    }

                                                    if (isExtra) {
                                                        return (
                                                            <span
                                                                key={cust.id || idx}
                                                                className="px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40 font-medium text-xs flex items-center gap-1"
                                                            >
                                                                <span className="text-amber-400 font-bold">+</span>
                                                                {cust.name}
                                                                {price > 0 && (
                                                                    <span className="text-amber-400 font-semibold ml-0.5">
                                                                        (+£{price.toFixed(2)})
                                                                    </span>
                                                                )}
                                                            </span>
                                                        );
                                                    }

                                                    return (
                                                        <span
                                                            key={cust.id || idx}
                                                            className="px-2 py-1 rounded bg-color2 text-gray-200 border border-color3 text-xs flex items-center gap-1"
                                                        >
                                                            <CheckCircle2 size={12} className="text-emerald-400 shrink-0" />
                                                            {cust.name}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Combo Meal Items Breakdown */}
                    {hasCombos && (
                        <div className="bg-color3/30 border border-amber-500/40 rounded-lg p-3.5 flex flex-col gap-3">
                            <div className="flex items-center justify-between border-b border-color3/60 pb-1.5">
                                <div className="text-xs font-semibold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Utensils size={14} /> Combo Items ({item.combo_items.length})
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                {item.combo_items.map((ci, cIdx) => {
                                    const ciName = ci.name || ci.display_name || ci.item_name || ci.menu_item?.name || "Item";
                                    const isReplaced = Boolean(ci.is_replaced);
                                    const activeExtra = Number(ci.price_difference ?? ci.replacement_extra_out_price ?? ci.replacement_extra_price ?? 0);
                                    const subCustomizations = getCustomizationList(ci);

                                    return (
                                        <div
                                            key={ci.id || ci.combo_item_unique_id || cIdx}
                                            className="bg-color2/80 rounded-lg p-2.5 border border-color3 flex flex-col gap-1.5"
                                        >
                                            <div className="flex items-center justify-between flex-wrap gap-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-semibold text-white">{cIdx + 1}. {ciName}</span>
                                                    {ci.variant?.name && (
                                                        <span className="text-color6 text-xs font-medium">
                                                            ({ci.variant.name})
                                                        </span>
                                                    )}
                                                    <span className="text-xs text-gray-400 bg-color3 px-1.5 py-0.5 rounded">
                                                        x{ci.quantity || 1}
                                                    </span>
                                                    {isReplaced && (
                                                        <span className="text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/40 px-1.5 py-0.5 rounded font-bold">
                                                            Replaced
                                                        </span>
                                                    )}
                                                </div>
                                                {isReplaced && activeExtra > 0 && (
                                                    <span className="text-xs text-amber-400 font-bold">
                                                        +£{activeExtra.toFixed(2)}
                                                    </span>
                                                )}
                                            </div>

                                            {isReplaced && ci.original_item && (
                                                <div className="text-xs text-gray-400 italic pl-3 border-l border-amber-500/40">
                                                    Original: {ci.original_item.display_name || ci.original_item.name}
                                                    {ci.original_item.variant?.name ? ` (${ci.original_item.variant.name})` : ""}
                                                </div>
                                            )}

                                            {subCustomizations.length > 0 && (
                                                <div className="flex flex-wrap gap-1 pt-1 pl-3 border-l border-color3">
                                                    {subCustomizations.map((cust, idx) => {
                                                        const isExtra = cust.isExtra;
                                                        const isCanceled = cust.isCanceled;
                                                        const price = Number(cust.price || 0);

                                                        if (isCanceled) {
                                                            return (
                                                                <span
                                                                    key={cust.id || idx}
                                                                    className="text-[11px] px-1.5 py-0.5 rounded bg-red-950/40 text-red-400 border border-red-800/50 line-through flex items-center gap-1"
                                                                    title="Removed / Canceled"
                                                                >
                                                                    <XCircle size={10} className="shrink-0" />
                                                                    {cust.name}
                                                                </span>
                                                            );
                                                        }

                                                        return (
                                                            <span
                                                                key={cust.id || idx}
                                                                className={`text-[11px] px-1.5 py-0.5 rounded ${
                                                                    isExtra
                                                                        ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                                                                        : "bg-color3 text-gray-300"
                                                                }`}
                                                            >
                                                                + {cust.name} {isExtra && price > 0 ? `(£${price.toFixed(2)})` : ""}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Mix & Match / CYO Items Breakdown */}
                    {hasMixAndMatch && !hasCombos && !hasSelectedCourses && (
                        <div className="bg-color3/30 border border-purple-500/40 rounded-lg p-3.5 flex flex-col gap-3">
                            <div className="flex items-center justify-between border-b border-color3/60 pb-1.5">
                                <div className="text-xs font-semibold text-purple-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Utensils size={14} /> Selected Selections
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                {(item.mix_and_match_items || item.selected_items || item.categories || []).map((entry, eIdx) => {
                                    if (Array.isArray(entry?.items)) {
                                        return (
                                            <div key={eIdx} className="bg-color2/70 p-2.5 rounded-lg border border-color3 flex flex-col gap-1.5">
                                                <div className="text-xs font-bold text-purple-300">{entry.category_name || `Category ${eIdx + 1}`}</div>
                                                <div className="flex flex-col gap-1 pl-2">
                                                    {entry.items.map((sub, sIdx) => (
                                                        <div key={sIdx} className="flex items-center justify-between text-xs text-gray-200">
                                                            <span>• {sub.name || sub.item_name || sub.display_name}</span>
                                                            <span className="text-color6 font-semibold">x{sub.quantity || 1}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }

                                    const entryName = entry.name || entry.item_name || entry.display_name || "Item";
                                    return (
                                        <div key={eIdx} className="bg-color2/70 p-2.5 rounded-lg border border-color3 flex items-center justify-between text-xs">
                                            <span className="text-white font-medium">• {entryName}</span>
                                            {entry.category_name && (
                                                <span className="text-gray-400 text-[11px]">{entry.category_name}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Item Notes / Special Instructions */}
                    {itemNote && (
                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex flex-col gap-1">
                            <div className="text-xs font-bold text-amber-400 flex items-center gap-1.5">
                                <AlertCircle size={14} /> Special Instructions / Notes
                            </div>
                            <div className="text-xs text-amber-100 bg-color2/80 p-2 rounded border border-amber-500/20 whitespace-pre-wrap">
                                {itemNote}
                            </div>
                        </div>
                    )}

                </div>

                {/* Footer */}
                <div className="px-5 py-3 bg-color3/60 border-t border-color3 flex items-center justify-end shrink-0">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2 bg-color6 hover:bg-color6/90 text-color1 font-bold rounded-lg transition cursor-pointer text-sm shadow"
                    >
                        Close
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
